import { ImFeature, ImSlumpProduct } from '@cemex-core/cmx-api';
import { PumpPrimerActions, PumpPrimerActionTypes } from './pump-primer.actions';
import {
  PP_ABD_MAX_VALUE,
  PP_ABD_DEFAULT_VALUE,
  PP_QTY_MIN_VALUE,
  PP_QTY_MAX_VALUE,
  PP_QTY_DEFAULT_VALUE,
  PP_PUMP_MED_VALUE,
  PP_PUMP_MED_MULTIPLE_VALUE,
} from 'app/shared/pumping/pumping.constant';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

/**
 * Pump Primer State
 * @export
 * @interface IPumpPrimerState
 *
 */
export interface IPumpPrimerState {
  maxPumpPrimerQuantity?: number;
  minPumpPrimerQuantity?: number;
  defaultPumpPrimerQuantity?: number;
  loading?: boolean;
  loaded?: boolean;
  defaultPumpPrimerArrivalBeforeDelivery?: number;
  maxPumpPrimerArrivalBeforeDelivery?: number;
  primerSlumps?: any[];
  unloadTypePumpValue?: string;
  unloadTypePumpValues?: Array<string>;
  primerSlumpProduct?: ImSlumpProduct;
}

export const initialState: IPumpPrimerState = {
  maxPumpPrimerQuantity: null,
  minPumpPrimerQuantity: null,
  defaultPumpPrimerQuantity: null,
  loading: false,
  loaded: false,
  defaultPumpPrimerArrivalBeforeDelivery: null,
  maxPumpPrimerArrivalBeforeDelivery: null,
  primerSlumps: null,
  unloadTypePumpValue: null,
  unloadTypePumpValues: null,
  primerSlumpProduct: null,
};

export function pumpPrimerReducer(state = initialState, { payload, type }: PumpPrimerActions): IPumpPrimerState {
  switch (type) {
    case PumpPrimerActionTypes.LOAD_PUMP_PRIMER_VALUES: {
      return {
        ...state,
        loading: true,
        loaded: false,
      } as IPumpPrimerState;
    }
    case PumpPrimerActionTypes.LOAD_PUMP_PRIMER_VALUES_SUCCESS: {
      const defaultItem = findItemByFeatureCode(payload, PP_QTY_DEFAULT_VALUE);
      const maxItem = findItemByFeatureCode(payload, PP_QTY_MAX_VALUE);
      const minItem = findItemByFeatureCode(payload, PP_QTY_MIN_VALUE);
      const ppabdDefault = findItemByFeatureCode(payload, PP_ABD_DEFAULT_VALUE);
      const ppabdMax = findItemByFeatureCode(payload, PP_ABD_MAX_VALUE);
      const unloadTypePumpSingleValue = findItemByFeatureCode(payload, PP_PUMP_MED_VALUE);
      const unloadTypePumpMultipleValue = findItemByFeatureCode(payload, PP_PUMP_MED_MULTIPLE_VALUE);
      const unloadTypePumpValue = fallbackValidationUnloadType(unloadTypePumpMultipleValue, unloadTypePumpSingleValue);
      const defaultQuantity = defaultItem ? defaultItem.featureValue : 0;
      const maxQuantity = maxItem ? maxItem.featureValue : 0;
      const minQuantity = minItem ? minItem.featureValue : 0;
      return {
        ...state,
        loaded: true,
        loading: false,
        defaultPumpPrimerQuantity: parseInt(defaultQuantity, 10),
        maxPumpPrimerQuantity: parseInt(maxQuantity, 10),
        minPumpPrimerQuantity: parseInt(minQuantity, 10),
        defaultPumpPrimerArrivalBeforeDelivery: _safe(() => Number(ppabdDefault.featureValue), undefined),
        maxPumpPrimerArrivalBeforeDelivery: _safe(() => Number(ppabdMax.featureValue), undefined),
        unloadTypePumpValue: _safe(() => unloadTypePumpSingleValue.featureValue, undefined),
        unloadTypePumpValues: unloadTypePumpValue,
      } as IPumpPrimerState;
    }
    case PumpPrimerActionTypes.LOAD_PRIMER_SLUMP_VALUES: {
      return {
        ...state,
        loading: true,
        loaded: false,
      } as IPumpPrimerState;
    }
    case PumpPrimerActionTypes.LOAD_PRIMER_SLUMP_VALUES_SUCCESS: {
      return {
        ...state,
        loaded: true,
        loading: false,
        primerSlumps: payload.slumps,
        primerSlumpProduct: payload.slumpProduct,
      } as IPumpPrimerState;
    }
    default: {
      return state;
    }
  }

  function findItemByFeatureCode(items, featureCode) {
    return items.find(({ featureCode: code }) => code === featureCode);
  }

  /**
   * Function that ensures that the PP_PUMP_MED_MULTIPLE_VALUE value is set correctly. Otherwise, use PP_PUMP_MED_VALUE value.
   * @param {ImFeature | undefined} uTypeMultiple - Multiple unload type values.
   * @param {ImFeature} uTypeSingle - Single unload type value.
   * @returns {Array<string>} - The unload types that will trigger the pump primer flow.
   */
  function fallbackValidationUnloadType(uTypeMultiple: ImFeature | undefined, uTypeSingle: ImFeature) {
    if (!!uTypeMultiple) {
      return uTypeMultiple.featureValue.split(',');
    } else {
      return [_safe(() => uTypeSingle.featureValue, undefined)];
    }
  }
}
